import {Predicate, ValueResolver, Validator, VisibilitySet} from '@/engine';
import {applyStripOptions} from '@/engine/utils/string';
import {SectionValidation, SectionValidationType, Validation, ValidationType} from '@/model/types';
import {CURRENT_YEAR_TOKEN, CurrentYearToken} from '@/model/types/constants';
import {getPredicate} from './predicates';
import {validateDate, normalizeDateInput, validateDayOfTheMonth, validateDayOfTheWeek} from './utils/date';

export const resolveNumericBound = (value: number | CurrentYearToken): number => {
  if (value === CURRENT_YEAR_TOKEN) {
    return new Date().getFullYear();
  }

  return value;
};

type ValidatorFactory<T extends Validation['type']> = (
  validation: Extract<Validation, {type: T}>,
  valueResolver: ValueResolver,
  visibilitySet: VisibilitySet,
) => Validator;
type SectionValidatorFactory<T> = (
  validation: Extract<SectionValidation, {type: T}>,
  valueResolver: ValueResolver,
  visibilitySet: VisibilitySet,
) => Predicate;

const MinValidator: ValidatorFactory<'min'> = ({value}) => {
  const min = resolveNumericBound(value);
  return (input = '') => Number(input) > min;
};

const MinEqualValidator: ValidatorFactory<'minEqual'> = ({value}) => {
  const minEqual = resolveNumericBound(value);
  return (input = '') => Number(input) >= minEqual;
};

const MaxValidator: ValidatorFactory<'max'> = ({value}) => {
  const max = resolveNumericBound(value);
  return (input = '') => Number(input) < max;
};

const MaxEqualValidator: ValidatorFactory<'maxEqual'> = ({value}) => {
  const maxEqual = resolveNumericBound(value);
  return (input = '') => Number(input) <= maxEqual;
};

const MaxSumValidator: ValidatorFactory<'maxSum'> = ({value, fields}, valueResolver) => {
  const max = typeof value === 'number' ? value : Number(valueResolver(value));
  // maxSum ignores input value when fields are filled to avoid double counting
  // This validation behaves more like a section validation than a field validation, but we keep it at the field level for clarity
  return (input = '') => {
    const sum = fields.reduce(
      (acc, field) => {
        if (field.includes('*')) {
          return acc + valueResolver<string[]>({$ref: field}).reduce((acc, value) => acc + Number(value), 0);
        } else {
          return acc + Number(valueResolver({$ref: field}));
        }
      },
      fields.length > 0 ? 0 : Number(input),
    );
    return sum <= max;
  };
};

const FixedLengthValidator: ValidatorFactory<'length'> = ({value: length, options}, valueResolver) => {
  const lengthResolved = typeof length === 'number' ? length : Number(valueResolver(length));
  return (input = '') => applyStripOptions(input, options?.strip).length === lengthResolved;
};

const MinLengthValidator: ValidatorFactory<'minLength'> = ({value: minLength, options}, valueResolver) => {
  const minLengthResolved = typeof minLength === 'number' ? minLength : Number(valueResolver(minLength));
  return (input = '') => applyStripOptions(input, options?.strip).length >= minLengthResolved;
};

const MaxLengthValidator: ValidatorFactory<'maxLength'> = ({value: maxLength, options}, valueResolver) => {
  const maxLengthResolved = typeof maxLength === 'number' ? maxLength : Number(valueResolver(maxLength));
  return (input = '') => applyStripOptions(input, options?.strip).length <= maxLengthResolved;
};

const RegExpValidator: ValidatorFactory<'regexp'> = ({value: exp}) => {
  return (input = '') => RegExp(exp).test(input);
};

const EmailValidator: ValidatorFactory<'email'> = () => {
  return (input = '') => /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(input);
};

const SameAsValidator: ValidatorFactory<'sameAs'> = ({value}, valueResolver) => {
  return (input = '') => input === valueResolver(value);
};

const NotInValidator: ValidatorFactory<'notIn'> = ({value}, valueResolver) => {
  return (input = '') => {
    const maybeArrayValue = valueResolver(value);

    const arrayValue = Array.isArray(maybeArrayValue) ? maybeArrayValue : [maybeArrayValue];

    return !arrayValue.includes(input);
  };
};

const ConditionValidator: ValidatorFactory<'condition'> = ({condition}, valueResolver, visibilitySet) => {
  const predicate = getPredicate(condition)(valueResolver, visibilitySet);
  return () => predicate();
};

const NASValidator: ValidatorFactory<'nas'> = ({options}) => {
  return (input = '') => {
    const cleanedInput = input.replace(/\D/g, '');
    if (!/^\d{9}$/.test(cleanedInput)) return false;
    if (cleanedInput.startsWith('8')) return false;

    if (!options?.allowZero && cleanedInput === '000000000') return false;

    // Check if the input is a valid NAS using Luhn's algorithm
    const check = cleanedInput
      .split('')
      .map(Number)
      .map((digit, index) => (index % 2 ? digit * 2 : digit))
      .join('')
      .split('')
      .map(Number)
      .reduce((sum, digit) => sum + digit, 0);

    return check % 10 === 0;
  };
};

const DateValidator: ValidatorFactory<'date'> = ({value}) => {
  return (input = '') => validateDate(value)(normalizeDateInput(input));
};

const DayOfTheMonthValidator: ValidatorFactory<'dayOfTheMonth'> = ({value}) => {
  return (input = '') => validateDayOfTheMonth(value)(normalizeDateInput(input));
};

const DayOfTheWeekValidator: ValidatorFactory<'dayOfTheWeek'> = ({value}) => {
  return (input = '') => validateDayOfTheWeek(value)(normalizeDateInput(input));
};

type ValidatorMap = {
  [T in ValidationType]: ValidatorFactory<T>;
};

const validatorMap: ValidatorMap = {
  min: MinValidator,
  minEqual: MinEqualValidator,
  max: MaxValidator,
  maxEqual: MaxEqualValidator,
  maxSum: MaxSumValidator,
  length: FixedLengthValidator,
  minLength: MinLengthValidator,
  maxLength: MaxLengthValidator,
  regexp: RegExpValidator,
  nas: NASValidator,
  email: EmailValidator,
  sameAs: SameAsValidator,
  date: DateValidator,
  dayOfTheMonth: DayOfTheMonthValidator,
  dayOfTheWeek: DayOfTheWeekValidator,
  notIn: NotInValidator,
  condition: ConditionValidator,
};

export const getValidator = <K extends ValidationType>(validation: Extract<Validation, {type: K}>) => {
  if (!validatorMap[validation.type]) {
    throw new Error(`Unknown validation type: ${validation.type}`);
  }

  return (valueResolver: ValueResolver, visibilitySet: VisibilitySet) =>
    validatorMap[validation.type](validation, valueResolver, visibilitySet);
};

const ConditionSectionValidator: SectionValidatorFactory<'condition'> = ({condition}, valueResolver, visibilitySet) => {
  const predicate = getPredicate(condition)(valueResolver, visibilitySet);
  return () => predicate();
};

const SumSectionValidator: SectionValidatorFactory<'sum'> = ({value, fields}, valueResolver, visibilitySet) => {
  return () => {
    const options = {visibilitySet: visibilitySet};
    const sum = fields.reduce((acc, field) => {
      if (field.includes('*')) {
        return acc + valueResolver<string[]>({$ref: field}, options).reduce((acc, value) => acc + Number(value), 0);
      } else {
        return acc + Number(valueResolver({$ref: field}, options));
      }
    }, 0);

    return sum === value;
  };
};

const DistinctSectionValidator: SectionValidatorFactory<'distinct'> = ({fields}, valueResolver, visibilitySet) => {
  return () => {
    const values: Set<string> = new Set();

    for (const field of fields) {
      const ref = {$ref: field};
      const options = {visibilitySet: visibilitySet};
      const fieldValues = field.includes('*') ? valueResolver<string[]>(ref, options) : [valueResolver(ref, options)];

      if (fieldValues.length !== 0) {
        const newValues = new Set(fieldValues);
        if (!isDisjointFrom(newValues, values) || fieldValues.length !== newValues.size) return false;

        for (const value of fieldValues) {
          values.add(value);
        }
      }
    }

    return true;
  };
};

const isDisjointFrom = (set1: Set<string>, set2: Set<string>) => {
  const union = new Set([...set1, ...set2]);
  return union.size === set1.size;
};

const GroupDistinctSectionValidator: SectionValidatorFactory<'groupDistinct'> = (
  {fields},
  valueResolver,
  visibilitySet,
) => {
  return () => {
    const options = {visibilitySet: visibilitySet};
    const fieldGroups = valueResolver<string[][]>(fields, options);

    for (const values of fieldGroups) {
      if (values.length !== new Set(values).size) {
        return false;
      }
    }

    return true;
  };
};

const GroupSumSectionValidator: SectionValidatorFactory<'groupSum'> = (
  {value, fields},
  valueResolver,
  visibilitySet,
) => {
  return () => {
    const options = {visibilitySet: visibilitySet};
    const sum = valueResolver<string[][]>(fields, options).map(group =>
      group.reduce((acc, value) => acc + Number(value), 0),
    );
    return sum.every(groupSum => groupSum === value);
  };
};

type SectionValidatorMap = {
  [T in SectionValidationType]: SectionValidatorFactory<T>;
};

const sectionValidatorMap: SectionValidatorMap = {
  condition: ConditionSectionValidator,
  sum: SumSectionValidator,
  groupSum: GroupSumSectionValidator,
  distinct: DistinctSectionValidator,
  groupDistinct: GroupDistinctSectionValidator,
};

export const getSectionValidator = <K extends SectionValidationType>(
  validation: Extract<SectionValidation, {type: K}>,
) => {
  if (!sectionValidatorMap[validation.type]) {
    throw new Error(`Unknown validation type: ${validation.type}`);
  }

  return (valueResolver: ValueResolver, visibilitySet: VisibilitySet) =>
    sectionValidatorMap[validation.type](validation, valueResolver, visibilitySet);
};
